import { type SqlBackend } from './sql.js';

/**
 * Replica schema mirrors the server-side DOSQL schema in apps/do-workspace
 * (deliberately — schema parity is the reason 11-tech-stack.md §5 picked
 * SQLite-WASM on both sides). Migrations are forward-only and idempotent.
 *
 * When the server schema changes, this file changes in lockstep. Until
 * the schema becomes contentious enough to warrant a shared `migrations`
 * package, the duplication is the cost of clarity at the two boundaries.
 */
export interface Migration {
  version: number;
  sql: string;
}

export const MIGRATIONS: Migration[] = [
  {
    version: 1,
    sql: `
      CREATE TABLE IF NOT EXISTS op_log (
        id              TEXT PRIMARY KEY,
        subject         TEXT NOT NULL,
        predicate       TEXT NOT NULL,
        payload         TEXT NOT NULL,
        base_version    TEXT,
        hlc_wall_ms     INTEGER NOT NULL,
        hlc_logical     INTEGER NOT NULL,
        author          TEXT NOT NULL,
        created_at_real TEXT NOT NULL
      );
      CREATE INDEX IF NOT EXISTS op_log_subject_predicate_hlc
        ON op_log (subject, predicate, hlc_wall_ms, hlc_logical);

      CREATE TABLE IF NOT EXISTS predicate_head (
        subject     TEXT NOT NULL,
        predicate   TEXT NOT NULL,
        head_op_id  TEXT NOT NULL,
        PRIMARY KEY (subject, predicate)
      );
    `,
  },
  {
    // I.A.1 (#23) — fact status on the cache. Mirrors apps/do-workspace
    // migration 0002.
    version: 2,
    sql: `
      ALTER TABLE predicate_head ADD COLUMN status TEXT NOT NULL DEFAULT 'canon';
      CREATE INDEX IF NOT EXISTS predicate_head_status
        ON predicate_head (status);
    `,
  },
  {
    // I.A.2 (#24) — fact owner. Mirrors apps/do-workspace migration 0003.
    version: 3,
    sql: `
      ALTER TABLE predicate_head ADD COLUMN owner_kind TEXT NOT NULL DEFAULT 'canon';
      ALTER TABLE predicate_head ADD COLUMN owner_viewer TEXT;
      CREATE INDEX IF NOT EXISTS predicate_head_owner
        ON predicate_head (owner_kind, owner_viewer);
    `,
  },
  {
    // I.A.3 (#25) — validity range. Mirrors apps/do-workspace migration 0004.
    version: 4,
    sql: `
      ALTER TABLE predicate_head ADD COLUMN valid_from INTEGER;
      ALTER TABLE predicate_head ADD COLUMN valid_to INTEGER;
      CREATE INDEX IF NOT EXISTS predicate_head_validity
        ON predicate_head (valid_from, valid_to);
    `,
  },
  {
    // I.E.2 (#39) — Entity name search index. Mirrors apps/do-workspace
    // migration version 6 / migrations/0002_search_name_index.sql.
    //
    // The replica intentionally skips version 5 (workspace_runtime is a
    // DOSQL-only concern — the GM client doesn't replicate the cursor row
    // because cursor state is local to the GM session). Version numbers
    // line up where the schemas are shared.
    version: 6,
    sql: `
      CREATE TABLE IF NOT EXISTS entity_name_index (
        entity_id        TEXT PRIMARY KEY,
        normalized_name  TEXT NOT NULL,
        display_name     TEXT NOT NULL
      );
      CREATE INDEX IF NOT EXISTS entity_name_index_normalized
        ON entity_name_index (normalized_name);
    `,
  },
  {
    // I.E.3 (#40) — FTS5 virtual table. Mirrors apps/do-workspace
    // migration version 7 / migrations/0003_fts.sql.
    version: 7,
    sql: `
      CREATE VIRTUAL TABLE IF NOT EXISTS fact_fts USING fts5(
        fact_id    UNINDEXED,
        entity_id  UNINDEXED,
        predicate  UNINDEXED,
        text_value,
        tokenize = 'unicode61 remove_diacritics 2'
      );
    `,
  },
  {
    // I.E.5 (#42) — Embedding store schema. Mirrors apps/do-workspace
    // migration version 8 / migrations/0004_embeddings.sql.
    version: 8,
    sql: `
      CREATE TABLE IF NOT EXISTS embedding_models (
        id            TEXT PRIMARY KEY,
        name          TEXT NOT NULL,
        dim           INTEGER NOT NULL CHECK (dim > 0),
        deprecated_at TEXT
      );
      CREATE TABLE IF NOT EXISTS embeddings (
        id          TEXT PRIMARY KEY,
        fact_id     TEXT NOT NULL,
        model_id    TEXT NOT NULL,
        vector      BLOB NOT NULL,
        created_at  TEXT NOT NULL,
        FOREIGN KEY (model_id) REFERENCES embedding_models(id)
      );
      CREATE INDEX IF NOT EXISTS embeddings_fact_id
        ON embeddings (fact_id);
      CREATE INDEX IF NOT EXISTS embeddings_model_id
        ON embeddings (model_id);
    `,
  },
  {
    // III.A.2 (#62) — per-Viewer sync-watermark mirror. The DOSQL
    // `viewer_watermarks` table is authoritative; this is the
    // client-side cache used for resume-on-reconnect (#62 AC: "Replica
    // stores the watermark alongside the op log").
    //
    // Mirrors apps/do-workspace migration version 9. Keyed by
    // viewer_key (`kind:id`); the workspace_id is implicit (one
    // replica per Workspace by convention).
    version: 9,
    sql: `
      CREATE TABLE IF NOT EXISTS viewer_watermarks (
        viewer_key    TEXT    PRIMARY KEY,
        hlc_wall_ms   INTEGER NOT NULL,
        hlc_logical   INTEGER NOT NULL,
        updated_at    TEXT    NOT NULL
      );
    `,
  },
  {
    // Global-HLC index on op_log. The replica folds the whole op log in
    // global HLC order (`SELECT * FROM op_log ORDER BY hlc_wall_ms, hlc_logical`)
    // and reads the tip on every reconnect; the v1 `(subject, predicate, …)`
    // index can't serve those, forcing a full scan + sort. Mirrors DOSQL
    // migration version 4. Keeps schema parity on the hot read path.
    version: 10,
    sql: `
      CREATE INDEX IF NOT EXISTS op_log_hlc
        ON op_log (hlc_wall_ms, hlc_logical);
    `,
  },
  {
    // ─── Durable submission outbox (audit fix — unacked local ops lost) ────────
    // `SyncClient` held its submit queue in memory only, so a reload between a
    // local append and the server accept ACK lost the queue — yet the op stayed
    // durably in `op_log` and kept folding as canon, never re-sent for server
    // arbitration. `confirmed_hlc` records the server-confirmed HLC of an op
    // (JSON `{wall_ms,logical}`); NULL means "locally authored, not yet
    // arbitrated". `SyncClient.connect()` lists NULL rows and re-enqueues them so
    // they re-flush. A locally-appended op starts NULL; a server op (from
    // applyServerOp — an accept round-trip or a peer's op) is confirmed on
    // arrival. This is a DOSQL-only mirror concern; the server op_log has no such
    // column (a server op is authoritative by definition).
    version: 11,
    sql: `ALTER TABLE op_log ADD COLUMN confirmed_hlc TEXT;`,
  },
];

interface VersionRow extends Record<string, unknown> {
  version: number;
}

export function applyMigrations(sql: SqlBackend): void {
  sql.exec(`CREATE TABLE IF NOT EXISTS schema_version (version INTEGER PRIMARY KEY);`);
  const applied = new Set(
    sql.all<VersionRow>(`SELECT version FROM schema_version ORDER BY version ASC;`).map((r) => r.version),
  );
  for (const m of MIGRATIONS) {
    if (applied.has(m.version)) continue;
    sql.exec(m.sql);
    sql.exec(`INSERT INTO schema_version (version) VALUES (?);`, m.version);
  }
}
